// hooks/useSilos.ts
import { useState, useEffect } from "react";
import { MapEngine } from "@/client/map/core/MapEngine";
import type { SiloPlatform } from "@/client/map/types";

export function useSilosSelection() {
  const [selectedTargets, setSelectedTargets] = useState<SiloPlatform[]>([]);
  const [allPlatforms, setAllPlatforms] = useState<SiloPlatform[]>([]);

  useEffect(() => {
    let interval: any;
    let cleanupBusListener: (() => void) | null = null;

    const syncSelection = () => {
      const manager = MapEngine.getInstance()?.getSiloManager();
      if (manager) {
        // Sync active user selections
        const currentSelected = manager.getSelectedSilos();
        setSelectedTargets(currentSelected);

        // Sync full operational system tracking data (Platforms, Silos, etc.)
        const currentAll = manager.getAllSiloPlatforms();
        setAllPlatforms(currentAll);
      }
    };

    const attachSelectionListener = () => {
      const manager = MapEngine.getInstance()?.getSiloManager();
      const eventBus = MapEngine.getInstance()?.getEventBus();

      if (manager && eventBus) {
        clearInterval(interval);

        // Run an immediate sync frame
        syncSelection();

        // Listen for internal explicit state triggers
        eventBus.on("silos:selected-silos-updated", syncSelection);
        // Also listen to raw batch data stream updates to keep values updated in real time!
        eventBus.on("data:silos-batch-updated", syncSelection);

        cleanupBusListener = () => {
          eventBus.off("silos:selected-silos-updated", syncSelection);
          eventBus.off("data:silos-batch-updated", syncSelection);
        };
      }
    };

    // Keep scanning until the engine manager mounts
    interval = setInterval(attachSelectionListener, 100);
    attachSelectionListener();

    return () => {
      clearInterval(interval);
      if (cleanupBusListener) cleanupBusListener();
    };
  }, []);

  const select = (stationId: string) => {
    const eventBus = MapEngine.getInstance()?.getEventBus();
    const manager = MapEngine.getInstance()?.getSiloManager();

    if (eventBus && manager) {
      // 1. Force the internal cache system to process the update
      manager.selectSilo?.(stationId);

      // 2. 🚀 THE MAGIC FIX: Fire the event that the map click uses!
      // This forces the manager's internal setupListeners block to fire immediately,
      // which handles setMapFeatureState and triggers silos:selected-silos-updated safely.
      eventBus.emit("silo:selected", { silo_id: stationId });
    }
  };

  const deselect = (stationId: string) => {
    const eventBus = MapEngine.getInstance()?.getEventBus();
    const manager = MapEngine.getInstance()?.getSiloManager();

    if (eventBus && manager) {
      // 1. Force the internal cache system to process the update
      manager.deselectSilo?.(stationId);

      // 2. Fire the event to clean up states cleanly
      eventBus.emit("silo:deselected", { silo_id: stationId });
    }
  };

  return {
    selectedTargets,
    allPlatforms,
    select,
    deselect,
  };
}
